import { parse } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { retry, takeUntil, timeout } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnCamera, SnCameraManagementType, SnLiveVideoUrls } from '@lib/api/entities';
import { SnSessionService } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { ITimelineDefinition, LOCAL_TIMELINE_TIMEOUT, TimelineService } from '@lib/timeline';
import { t } from '@lib/translation';
import { SnVaultDialogComponent } from '@lib/vault';
import { SnAbilityService } from 'libs/ability/src/lib/services';

@Component({
  selector: 'sn-video-player-clip',
  templateUrl: './video-player-clip.control.html',
  styleUrls: ['./video-player-clip.control.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPlayerClipControl implements OnInit, OnDestroy {
  get isExternal(): boolean {
    return this.camera.managementType === SnCameraManagementType.External;
  }

  get isEagleEye(): boolean {
    if (!this.camera?.externalType) {
      return;
    }
    return this.camera.externalType?.toLowerCase() === 'eagleeyenetworks';
  }

  @Input()
  public cameraId: string;

  public form: UntypedFormGroup = new UntypedFormGroup({
    startTime: new UntypedFormControl(0),
    endTime: new UntypedFormControl(0),
    startDisplay: new UntypedFormControl('', Validators.required),
    endDisplay: new UntypedFormControl('', Validators.required),
  });

  public date = new Date();
  public start: Date;
  public end: Date;
  public time: Date;
  public isLocal: boolean;
  public startingTime: number = null;
  public isPlaying: boolean = false;
  public clipLoading: boolean;
  public toggleDisabledField: boolean = true;
  public errorTimer: boolean = false;
  public isFullScreen: boolean;
  public results: any[] = [];
  public activeResult: any;
  public percentDone = 0;
  public secondsLeft = 0;
  public fileSize = 0;
  public downloadedSize = 0;
  public videoCurrentTime: number;

  public src: any;
  public isDownloading: boolean = false;

  public camera$: Observable<SnCamera>;
  public cameraTimeline$: Observable<ITimelineDefinition>;
  public cameraTimeline: ITimelineDefinition;

  public exportDisabled: boolean;
  public camera: SnCamera;
  public formModel: any = {};

  public fullScreen: boolean;
  public formFields: SnFormFieldConfig[];
  public loadingData = true;

  public readonly canDownload: boolean;

  private readonly _destroy$ = new Subject<void>();
  private _setStartTime: number;
  private _setEndTime: number;
  private _startDate: Date;
  private _endDate: Date;

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _timelineService: TimelineService,
    private readonly _matDialogRef: MatDialogRef<VideoPlayerClipControl>,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _toastrService: ToastrService,
    private readonly _ability: SnAbilityService,
    private readonly _sessionService: SnSessionService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.canDownload = this._ability.can({
      subject: 'incidents/recording',
      action: 'can_download',
    });
  }

  public ngOnInit(): void {
    this.data.fullscreen$.pipe(takeUntil(this._destroy$)).subscribe((fullscreen: boolean) => {
      if (!fullscreen && !this.data.alwaysOn) {
        this._matDialogRef.close();
      }
    });
    this.camera = new SnCamera(this.data.camera);
    this.cameraId = this.data.camera.id;
    const timestamp: number = this.data.timeStamp;
    this._startDate = new Date(timestamp - 120 * 1000);
    this._endDate = new Date(timestamp + 30 * 1000);
    if (!this.camera.isFususCamera) {
      this.src = this.isExternal
        ? this.camera.liveVideoUrls
        : new SnLiveVideoUrls({
            main: this.camera.playbackUrls.main.toStreamSourceList(null),
          });
      this._getTimeline();
    } else {
      const url = this.data.camera.playbackUrls?.main.local.management;
      this._timelineService
        .getTimelineData(url)
        .pipe(takeUntil(this._destroy$))
        .subscribe((res: ITimelineDefinition[]) => {
          const length = res[0].timeline.length - 1;
          const path = res[0].timeline[length].file_path;
          this.src = new SnLiveVideoUrls({
            main: this.data.camera.playbackUrls.main.toFususStreamSourceList(path, this._startDate),
          });
          this._changeDetector.detectChanges();
        });
    }
    this._setForm();
    this._changeDetector.detectChanges();

    this.form.patchValue({
      startTime: this._startDate,
      startDisplay: this._startDate.toLocaleTimeString(),
      endTime: this._endDate,
      endDisplay: this._endDate.toLocaleTimeString(),
    });

    this._changeDetector.detectChanges();
  }

  public submitForm(): void {
    const params: any = {};
    const start = this.form.get('start');
    params.created_timestamp__gte = start.value ? Math.floor(start.value.getTime() / 1000) : '';
    const end = this.form.get('end');
    params.created_timestamp__lte = end.value ? Math.ceil(end.value.getTime() / 1000) : '';
    this._matDialogRef.close(params);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public dateChange(): void {
    this.time = new Date(this.date.getTime());
    this._changeDetector.detectChanges();
  }

  public startClip(): void {
    this._setStartTime = this.time.getTime();
    this.errorTimer = false;
    this.form.patchValue({
      startTime: this.time.getTime(),
      startDisplay: this.time.toLocaleTimeString(),
    });
  }

  public setTimer(e: any, type: string): void {
    const time = parse(e, 'p', this.date);
    this._setStartTime = time.getTime();
    if (type === 'start') {
      this.form.patchValue({
        startTime: time.getTime(),
        startDisplay: time.toLocaleTimeString(),
      });
    } else {
      this.form.patchValue({
        endTime: time.getTime(),
        endDisplay: time.toLocaleTimeString(),
      });
    }
  }

  public endClip(): void {
    this._setEndTime = this.time.getTime();
    this._checkTimer();
    if (!this.errorTimer) {
      this.form.patchValue({
        endTime: this.time.getTime(),
        endDisplay: this.time.toLocaleTimeString(),
      });
    }
  }

  public close(): void {
    this._matDialogRef.close();
  }

  public getProgress($event: boolean): void {
    this.clipLoading = $event;
    this._changeDetector.detectChanges();
  }

  public checkTime(): void {
    const start = Math.floor(this.form.get('start').value / 1000);
    const end = Math.ceil(this.form.get('end').value / 1000);
    if (end - start >= 21600) {
      this.exportDisabled = true;
    }
  }

  public addToVault(cameraId: string): void {
    this.clipLoading = true;
    const data = this.form.value;
    const start = Math.floor(this.form.get('start').value / 1000);
    const end = Math.ceil(this.form.get('end').value / 1000);
    this.checkTime();
    if (!this.exportDisabled) {
      const vaultIncidents = data.vaults || [];
      const playerRef = this.data.playerRef;
      this._dialog
        .open(SnVaultDialogComponent, {
          data: {
            name: this.camera.name,
            cameraId,
            start,
            end,
            vaultIncidents,
            playerRef,
          },
        })
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this.clipLoading = false;
          this._matDialogRef.close();
          this._changeDetector.detectChanges();
        });
    } else {
      this._toastrService.error(t('cameras.clip.errors.cannotExportLongVideo'));
      this._matDialogRef.close();
    }
  }

  private _checkTimer(): void {
    this.errorTimer = false;
    const timer = this._setStartTime <= this._setEndTime;
    if (!timer) {
      this.errorTimer = true;
      this._toastrService.error(t('cameras.clip.errors.endTimeAfterStartTime'));
    }
  }

  private _getTimeline(): void {
    const urlSrc = this._sessionService.isFederal
      ? this.camera.prerecordingUrls?.export
      : this.isExternal
      ? this.camera.prerecordingUrls?.management
      : this.camera.playbackUrls?.main?.local?.management;

    this._timelineService
      .getTimelineData(urlSrc)
      .pipe(timeout(LOCAL_TIMELINE_TIMEOUT), takeUntil(this._destroy$))
      .subscribe({
        next: (data: any) => {
          this.isLocal = true;
          this.cameraTimeline =
            this.isExternal && !this.isEagleEye ? data.result.timelines : data[0];
          this.loadingData = false;
          this._generateTimepickerFields();
          this._changeDetector.detectChanges();
        },
        error: () => {
          this.isLocal = false;
          this._getCloudTimeline();
        },
      });
  }

  private _getCloudTimeline(): void {
    const urlSrc = this.isExternal
      ? this.camera.prerecordingUrls.management
      : this.camera.playbackUrls.main.cloud.management;

    this._timelineService
      .getTimelineData(urlSrc)
      .pipe(timeout(1500), retry(3), takeUntil(this._destroy$))
      .subscribe({
        next: (data: any) => {
          this.isLocal = false;
          this.cameraTimeline =
            this.isExternal && !this.isEagleEye ? data.result.timelines : data[0];
          this.loadingData = false;
          this._generateTimepickerFields();
          this._changeDetector.detectChanges();
        },
        error: () => {
          this.isLocal = false;
          this._getCloudTimeline();
        },
      });
  }

  private _setForm(): void {
    const timestamp: number = this.data.timeStamp;
    this._startDate = new Date(timestamp - 120 * 1000);
    this._endDate = new Date(timestamp + 30 * 1000);
    this.formModel.start = this._startDate;
    this.formModel.end = this._endDate;
    this.formFields = this._generateTimepickerFields();
  }

  // TODO : For calendar work https://taserintl.atlassian.net/browse/FWX-3070
  private _generateTimepickerFields(): SnFormFieldConfig[] {
    return [
      {
        key: 'start',
        type: 'datetimepicker',
        templateOptions: {
          panelClass: 'availability',
          label: t('cameras.clip.startTime'),
          placeholder: this._startDate.toLocaleDateString(),
          appearance: 'fill',
          hidden: !this.cameraTimeline,
          minDate: () => {
            if (!this.cameraTimeline) {
              return null;
            }
            return new Date(this.cameraTimeline.timeline[0].start * 1000);
          },

          maxDate: () => {
            if (!this.cameraTimeline) {
              return null;
            }
            const timeline = this.cameraTimeline.timeline;
            return new Date(timeline[timeline.length - 1].end * 1000);
          },
        },
      },
      {
        key: 'end',
        type: 'datetimepicker',
        templateOptions: {
          panelClass: 'availability',
          label: t('cameras.clip.endTime'),
          placeholder: this._endDate.toLocaleDateString(),
          appearance: 'fill',
          hidden: !this.cameraTimeline,
          minDate: () => {
            if (!this.cameraTimeline) {
              return null;
            }
            return new Date(this.cameraTimeline.timeline[0].start * 1000);
          },

          maxDate: () => {
            if (!this.cameraTimeline) {
              return null;
            }
            const timeline = this.cameraTimeline.timeline;
            return new Date(timeline[timeline.length - 1].end * 1000);
          },
        },
      },
    ];
  }
}
